import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MachineModelDto } from 'stores/configuration/machinery/machine-models-store';
import { ProfessionDto } from 'stores/configuration/profiles/professions-store';
import { ItemReference } from 'stores/dataStore';
import TailwindMultiEventTypeEditor from 'widgets/bussiness/multiple-selector/multi-event-type-editor-tw';
import TailwindMultipleMachineModelEditor from 'widgets/bussiness/multiple-selector/multiple-machine-model-editor-tw';
import TailwindMultipleProfessionsEditor from 'widgets/bussiness/multiple-selector/multiple-professions-editor-tw';
import TailwindClearableTimerInput from 'widgets/editors/tailwind-clearable-timer-input';
import TailwindTrainingLevelEditor from 'widgets/bussiness/training-level-editor-tw';
import TailwindMultipleLocationEditor from 'widgets/bussiness/multiple-selector/multiple-location-editor-tw';
import { LocationDto } from 'stores/configuration/locations/locations-store';
import TailwindDeliveryMethodEditor from 'widgets/bussiness/delivery-method-editor-tw';
import { DateTimeService } from 'services/datetime-service';
import { DateInput } from 'widgets/form/dateInput';
import TailwindCategoryFormEditor from 'widgets/bussiness/category-form-editor-tw';
import { Category } from 'stores/configuration/events-n-requests/non-machine-related/clusters-store';
import { RequestStatus } from 'stores/requests/requests-store';
import { PriorityDto } from 'stores/configuration/events-n-requests/priorities-store';
import TailwindMultiplePriorityEditor from 'widgets/bussiness/multiple-selector/multiple-priority-editor-tw';
import TailwindMachineRelatedEditor from 'widgets/bussiness/machine-related-editor-tw';
import TailwindMultipleRequestStatusEditor from 'widgets/bussiness/multiple-selector/multiple-request-status-editor-tw';

interface FilterScreenProps {
  showActiveFilters?: boolean;
  values?: any[];
  onClose: (value: boolean) => void;
  onSave?: (value: any) => void;
}

const FilterScreen: React.FC<FilterScreenProps> = ({ values, showActiveFilters, onClose, onSave }) => {
  const [friendlyId, setFriendlyId] = useState<string>(values['friendlyId'] || '');
  const [eventTypeList, setEventTypeList] = useState<ItemReference[]>(values['eventType'] || []);
  const [title, setTitle] = useState<string>(values['title'] || '');
  const [machinesModel, setMachinesModel] = useState<MachineModelDto[]>(values['machinesModel'] || []);
  const [rolesList, setRolesList] = useState<ProfessionDto[]>(values['profession'] || []);
  const [trainingLevel, setTrainingLevel] = useState<ItemReference>(values['trainingLevelId'] || null);
  const [requestingLocationList, setRequestingLocationList] = useState<LocationDto[]>(values['requests'] || []);
  const [deliveryMethod, setDeliveryMethod] = useState<ItemReference>(values['deliveryMethodId'] || null);
  const [startDate, setStartDate] = useState<string>(values['startDate'] || '');
  const [endDate, setEndDate] = useState<string | null>(values['endDate'] || '');
  const [category, setCategory] = useState<Category>(values['category'] || null);
  const [machineRelatedOpt, setMachineRelatedOpt] = useState<ItemReference>(values['isMachineRelated'] || null);
  const [priorityList, setPriorityList] = useState<PriorityDto[]>(values['priority'] || []);
  const [requestStatus, setRequestStatus] = useState<RequestStatus[]>(values['status'] || []);
  const [filters, setFilters] = useState<any[]>(values || []);

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState<number | 'auto'>('auto');

  const [dropdownStates, setDropdownStates] = useState<{ [key: string]: boolean }>({
    status: false,
    priority: false,
    isMachineRelated: false,
    category: false,
    deliveryMethodId: false,
    requests: false,
    trainingLevelId: false,
    machinesModel: false,
    profession: false
  });

  const handleDropdownOpen = (key: string, open: boolean) => {
    setDropdownStates(prev => ({ ...prev, [key]: open }));
  };

  useEffect(() => {
    if (Object.values(dropdownStates).some(open => open) && containerRef.current) {
      setContainerHeight(containerRef.current.scrollHeight);
    } else {
      setContainerHeight('auto');
    }
  }, [dropdownStates]);

  const handleFilters = (value, fieldName) => {
    const filtersCopy = { ...filters };
    filtersCopy[fieldName] = value;
    setFilters(filtersCopy);
  };

  const onRequestIDFilterChanged = (value: string) => {
    handleFilters(value, 'friendlyId');
    setFriendlyId(value);
  };

  const onEventTypeFilterChanged = (value, fieldName) => {
    handleFilters(value, fieldName);
    setEventTypeList(value);
  };

  const onRequestTitleFilterChanged = (value: string) => {
    handleFilters(value, 'title');
    setTitle(value);
  };

  const onMachineModelFilterChanged = (value: MachineModelDto[], fieldName: string) => {
    handleFilters(value, fieldName);
    setMachinesModel(value);
  };

  const onRolesListChange = (value: ProfessionDto[], fieldName: string) => {
    handleFilters(value, fieldName);
    setRolesList(value);
  };

  const onTrainingLevelChanged = (value: ItemReference, fieldName: string) => {
    handleFilters(value, fieldName);
    setTrainingLevel(value);
  };

  const onRequestingLocationListChange = (value: LocationDto[], fieldName: string) => {
    handleFilters(value, fieldName);
    setRequestingLocationList(value);
  };

  const onDeliveryMethodChanged = (value: ItemReference, fieldName: string) => {
    handleFilters(value, fieldName);
    setDeliveryMethod(value);
  };

  const startDateFilterChanged = (_, value: string) => {
    handleFilters(DateTimeService.toString(value), 'startDate');
    setStartDate(DateTimeService.toString(value));
  };

  const endDateFilterChanged = (_, value: string) => {
    handleFilters(DateTimeService.toString(value), 'endDate');
    setEndDate(DateTimeService.toString(value));
  };

  const onCategoryChange = (value: Category, fieldName: string) => {
    handleFilters(value, fieldName);
    setCategory(value);
  };

  const onIsMachineRelatedChanged = (value: ItemReference, fieldName: string) => {
    handleFilters(value, fieldName);
    setMachineRelatedOpt(value);
  };

  const onPriorityListFilterChange = (value: PriorityDto[], fieldName: string) => {
    handleFilters(value, fieldName);
    setPriorityList(value);
  };

  const onStatusFilterChange = (value: RequestStatus[], fieldName: string) => {
    handleFilters(value, fieldName);
    setRequestStatus(value);
  };

  const onSaveClickHandler = () => {
    onSave(filters);
  };

  const onClearAllFilters = () => {
    setFriendlyId('');
    setEventTypeList([]);
    setTitle('');
    setMachinesModel([]);
    setRolesList([]);
    setTrainingLevel(null);
    setRequestingLocationList([]);
    setDeliveryMethod(null);
    setStartDate('');
    setEndDate('');
    setCategory(null);
    setMachineRelatedOpt(null);
    setPriorityList([]);
    setRequestStatus([]);
    setFilters([]);
    if ((filters || []).length > 0) {
      onClose(false);
    }
  };

  const { t } = useTranslation();

  return (
    <>
      <div ref={containerRef} style={{ height: containerHeight, transition: 'height 0.3s' }} className="overflow-y-auto bg-white p-4">
        <button onClick={() => onClose(true)} className="flex items-center gap-1 text-sm text-black-600 mb-2">
          ← Back
        </button>
        <h3 className="text-lg font-semibold mb-4">Filters</h3>

        <div className="mb-3">
          <label className="block text-sm text-gray-700 mb-1">{t('Request ID')}</label>
          <TailwindClearableTimerInput
            onKeyPress={e => e.key === 'Enter' && e.preventDefault()}
            className="w-full p-2 border border-gray-300 rounded"
            placeholder={t('Request ID')}
            onChange={onRequestIDFilterChanged}
            value={friendlyId}
          />
        </div>

        <div className="mb-3">
          <label className="block text-sm text-gray-700 mb-1">Event Type</label>
          <TailwindMultiEventTypeEditor
            className="w-full p-2 border border-gray-300 rounded"
            schedulerRender
            placeholder={t('Event Type')}
            useOriginalEventTypeIdAsValue
            value={eventTypeList.map(eTI => eTI)}
            onChange={value => onEventTypeFilterChanged(value, 'eventType')}
            loadDataOnOpen={true}
            onFocus={() => handleDropdownOpen('eventType', true)}
            onBlur={() => handleDropdownOpen('eventType', false)}
          />
        </div>

        <div className="mb-3">
          <label className="block text-sm text-gray-700 mb-1">{t('Request Title')}</label>
          <TailwindClearableTimerInput
            onKeyPress={e => e.key === 'Enter' && e.preventDefault()}
            className="filter margin-top-0 request__title-input"
            placeholder={t('Request Tittle')}
            onChange={onRequestTitleFilterChanged}
            value={title}
          />
        </div>

        <div className="mb-3">
          <label className="block text-sm text-gray-700 mb-1">{t('Machine Model')}</label>
          <TailwindMultipleMachineModelEditor
            className="w-full p-2 border border-gray-300 rounded"
            placeholder={t('Machine Model')}
            value={machinesModel}
            clearable
            onChange={value => onMachineModelFilterChanged(value, 'machinesModel')}
            loadDataOnOpen={true}
            onFocus={() => handleDropdownOpen('machinesModel', true)}
            onBlur={() => handleDropdownOpen('machinesModel', false)}
          />
        </div>

        <div className="mb-3">
          <label className="block text-sm text-gray-700 mb-1">{t('Roles')}</label>
          <TailwindMultipleProfessionsEditor
            clearable
            className="w-full p-2 border border-gray-300 rounded"
            placeholder={t('Roles')}
            value={rolesList}
            onChange={value => onRolesListChange(value, 'profession')}
            loadDataOnOpen={true}
            onFocus={() => handleDropdownOpen('profession', true)}
            onBlur={() => handleDropdownOpen('profession', false)}
          />
        </div>

        <div className="mb-3">
          <label className="block text-sm text-gray-700 mb-1">{t('Training Level')}</label>
          <TailwindTrainingLevelEditor
            placeholder={t('Training Level')}
            className="w-full p-2 border border-gray-300 rounded"
            nullable
            value={trainingLevel}
            onChange={value => onTrainingLevelChanged(value, 'trainingLevelId')}
            loadDataOnOpen={true}
            onFocus={() => handleDropdownOpen('trainingLevelId', true)}
            onBlur={() => handleDropdownOpen('trainingLevelId', false)}
          />
        </div>

        <div className="mb-3">
          <label className="block text-sm text-gray-700 mb-1">{t('Requesting Location')}</label>
          <TailwindMultipleLocationEditor
            clearable
            className="w-full p-2 border border-gray-300 rounded"
            placeholder={t('Requesting Location')}
            value={requestingLocationList}
            onChange={value => onRequestingLocationListChange(value, 'requests')}
            showCountry={true}
            loadDataOnOpen={true}
            onFocus={() => handleDropdownOpen('requests', true)}
            onBlur={() => handleDropdownOpen('requests', false)}
          />
        </div>

        <div className="mb-3">
          <label className="block text-sm text-gray-700 mb-1">{t('Delivery Method')}</label>
          <TailwindDeliveryMethodEditor
            placeholder={t('Delivery Method')}
            className="w-full p-2 border border-gray-300 rounded"
            nullable
            value={deliveryMethod}
            onChange={value => onDeliveryMethodChanged(value, 'deliveryMethodId')}
            loadDataOnOpen={true}
            onFocus={() => handleDropdownOpen('deliveryMethodId', true)}
            onBlur={() => handleDropdownOpen('deliveryMethodId', false)}
          />
        </div>

        <div className="mb-3">
          <label className="block text-sm text-gray-700 mb-1">{t('Desired Start Date')}</label>
          <DateInput
            className="w-full p-2 border border-gray-300 rounded"
            value={startDate || null}
            onChange={startDateFilterChanged}
            placeholder={t('Desired Start Date')}
            clearable
            onClear={startDateFilterChanged}
          />
        </div>

        <div className="mb-3">
          <label className="block text-sm text-gray-700 mb-1">{t('Desired End Date')}</label>
          <DateInput
            placeholder={t('Desired End Date')}
            className="w-full p-2 border border-gray-300 rounded"
            value={endDate || null}
            onChange={endDateFilterChanged}
            initialValue={startDate || null}
            minDate={startDate || null}
            clearable
          />
        </div>

        <div className="mb-3">
          <label className="block text-sm text-gray-700 mb-1">{t('Category')}</label>
          <TailwindCategoryFormEditor
            readOnly={false}
            className="w-full p-2 border border-gray-300 rounded"
            value={category}
            onChange={value => onCategoryChange(value, 'category')}
            clearable
            placeholder={t('Category')}
            onFocus={() => handleDropdownOpen('category', true)}
            onBlur={() => handleDropdownOpen('category', false)}
          />
        </div>

        <div className="mb-3">
          <label className="block text-sm text-gray-700 mb-1">{t('Machine Related')}</label>
          <TailwindMachineRelatedEditor
            placeholder={t('Machine Related')}
            className="w-full p-2 border border-gray-300 rounded"
            nullable
            value={machineRelatedOpt}
            onChange={value => onIsMachineRelatedChanged(value, 'isMachineRelated')}
            onFocus={() => handleDropdownOpen('isMachineRelated', true)}
            onBlur={() => handleDropdownOpen('isMachineRelated', false)}
          />
        </div>

        <div className="mb-3">
          <label className="block text-sm text-gray-700 mb-1">{t('Priority')}</label>
          <TailwindMultiplePriorityEditor
            className="w-full p-2 border border-gray-300 rounded"
            placeholder={t('Priority')}
            value={priorityList}
            clearable
            onChange={value => onPriorityListFilterChange(value, 'priority')}
            loadDataOnOpen={true}
            onFocus={() => handleDropdownOpen('priority', true)}
            onBlur={() => handleDropdownOpen('priority', false)}
          />
        </div>

        <div className="mb-3">
          <label className="block text-sm text-gray-700 mb-1">Status</label>
          <TailwindMultipleRequestStatusEditor
            clearable
            statusFilterActive={showActiveFilters}
            placeholder={t('Status')}
            value={requestStatus}
            onChange={value => onStatusFilterChange(value, 'status')}
            onFocus={() => handleDropdownOpen('status', true)}
            onBlur={() => handleDropdownOpen('status', false)}
          />
        </div>
      </div>

      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 58,
          right: 11,
          background: 'white',
          borderTop: '1px solid #ddd',
          padding: '0.5rem 1rem',
          zIndex: 1000
        }}
        className="flex gap-2"
      >
        <button className="flex-1 bg-primary-600 text-white py-2 rounded" onClick={() => onSaveClickHandler()}>
          Apply filters
        </button>
        <button className="flex-1 bg-primary-600 text-white py-2 rounded" onClick={() => onClearAllFilters()}>
          Clear all filters
        </button>
      </div>
    </>
  );
};

export default FilterScreen;
